import { createFileRoute } from "@tanstack/react-router";
import { useLocation } from "@tanstack/react-router";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientPaginatedResponse } from "api-types/common/commonResponses";
import type { ClientFeatureFlagDefinition } from "api-types/featureFlags/featureFlagsResponses";
import type { ClientFlagData } from "api-types/metrics/metricsResponses";
import type { ClientFeatureFlagStats } from "api-types/metrics/metricsResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { DOC_URLS, FALLBACK_VALUE, WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { getClientEnvironment } from "common/utils/env";
import { filterList } from "common/utils/search";
import { camelCaseToTitleCase } from "common/utils/strings";
import featureFlagOverviewStyles from "lib/components/admin/builders/AdminBuilderDetail.module.scss";
import featureFlagListStyles from "lib/components/admin/builders/AdminBuilderList.module.scss";
import AdminPage from "lib/components/admin/builders/AdminPage";
import AdminTableHeader from "lib/components/admin/builders/AdminTableHeader";
import BuilderFeatureFlagsList from "lib/components/admin/features/BuilderFeatureFlagsList";
import FeatureFlagDefinitionEditor from "lib/components/admin/features/FeatureFlagDefinitionEditor";
import { settingToDisplayValue, mapFeatureStatusToDisplayTextAndColor } from "lib/components/admin/features/helpers";
import UserFeatureFlagsList from "lib/components/admin/features/UserFeatureFlagsList";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import SplitView from "lib/components/layout/SplitView";
import { getFeatureFlagsURL } from "lib/endpointHelpers";
import { useFetch, usePaginatedFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { userIsSystemAdmin } from "lib/security/shared";
import { AdminBuilderContext } from "lib/state/AdminBuilderContextProvider";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteFeatureFlag } from "lib/storage/featureFlagStorage";
import { useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import Button from "ui/buttons/Button";
import Capsule from "ui/components/Capsule";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import HorizontalRule from "ui/components/HorizontalRule";
import Link from "ui/components/Link";
import Loading from "ui/components/Loading";
import TabbedMenu from "ui/components/TabbedMenu";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import SearchInput from "ui/search/SearchInput";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import TextWithTooltipIcon from "ui/text/TextWithTooltipIcon";
import withAuthentication from "../../components/withAuthentication";
import styles from "./features.module.scss";

type AdminFeatureFlagsPageTab = "settings" | "builders" | "users" | "metrics";

const METRIC_CARD_CONFIG: Array<{
  key:
    | "effectiveEnabledBuilders"
    | "effectiveEnabledUsers"
    | "explicitEnabledBuilders"
    | "explicitEnabledUsers"
    | "inheritedDefaultBuilders"
    | "inheritedDefaultUsers";
  label: string;
  tooltip: string;
}> = [
  {
    key: "effectiveEnabledBuilders",
    label: "Effective Enabled Builders",
    tooltip:
      "Builders currently receiving this flag as enabled. Includes explicit builder enables and builders inheriting from a default-enabled flag.",
  },
  {
    key: "effectiveEnabledUsers",
    label: "Effective Enabled Users",
    tooltip:
      "Users currently receiving this flag as enabled. Includes explicit user enables and users inheriting from a default-enabled flag.",
  },
  {
    key: "explicitEnabledBuilders",
    label: "Explicit Enabled Builders",
    tooltip: "Builders with an explicit flag value of Enabled saved on the builder record.",
  },
  {
    key: "explicitEnabledUsers",
    label: "Explicit Enabled Users",
    tooltip: "Users with an explicit flag value of Enabled saved on the user record.",
  },
  {
    key: "inheritedDefaultBuilders",
    label: "Inheriting Default Builders",
    tooltip: "Builders with no explicit builder override for this flag, so they inherit the default-enabled behavior.",
  },
  {
    key: "inheritedDefaultUsers",
    label: "Inheriting Default Users",
    tooltip: "Users with no explicit user override for this flag, so they inherit the default-enabled behavior.",
  },
];

function getFeatureDisplayName(featureDefinition: Pick<ClientFeatureFlagDefinition, "key" | "displayName">): string {
  return featureDefinition.displayName ?? camelCaseToTitleCase(featureDefinition.key);
}

function AdminFeatureFlagsPage() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("all");
  const [stalenessFilter, setStalenessFilter] = useState<string>("all");
  const [lifecycleFilter, setLifecycleFilter] = useState<string>("active");
  const [showDeleteRetiredModal, setShowDeleteRetiredModal] = useState(false);
  const [isDeletingRetired, setIsDeletingRetired] = useState(false);
  const { feature } = useAppRouterParams();
  const { user } = useUserContext();
  const { selectedFeature, setSelectedFeature } = useContext(AdminBuilderContext);
  const { server: environment } = getClientEnvironment();
  const { tab, menuItems } = useTabbedMenu<AdminFeatureFlagsPageTab>(["settings", "builders", "users", "metrics"]);
  const {
    data: users,
    loading: usersLoading,
    mutate: mutateUsers,
    setSearch: setUserSearch,
    orderBy,
    setOrderBy,
    pagination,
    setPagination,
  } = usePaginatedFetch<ClientUserHydrated>(`${WEB_API_BASE_PATH}/users`);
  const {
    data: builders,
    loading: buildersLoading,
    mutate: mutateBuilders,
  } = useFetch<ClientPaginatedResponse<ClientBuilder>>(`${WEB_API_BASE_PATH}/builders?limit=1000`);
  const {
    data: featureDefinitions,
    loading: featureDefinitionsLoading,
    mutate: mutateFeatureDefinitions,
  } = useFetch<ClientFeatureFlagDefinition[]>(getFeatureFlagsURL({ includeRetired: lifecycleFilter === "retired" }));
  const { data: flagData, loading: flagDataLoading } = useFetch<ClientFlagData>(`${WEB_API_BASE_PATH}/metrics/flags`);

  const leftPaneLoading = featureDefinitionsLoading && !featureDefinitions;
  const rightPaneLoading =
    featureDefinitionsLoading ||
    (tab === "builders" && buildersLoading) ||
    (tab === "users" && usersLoading) ||
    (tab === "metrics" && flagDataLoading);

  const selectedFeatureDefinition = useMemo(() => {
    if (!selectedFeature) {
      return null;
    }

    return featureDefinitions?.find((item) => item.key === selectedFeature) ?? null;
  }, [featureDefinitions, selectedFeature]);

  const selectedFeatureMetrics: ClientFeatureFlagStats | null = useMemo(() => {
    if (!selectedFeature) {
      return null;
    }

    return flagData?.featureFlags?.[selectedFeature] ?? null;
  }, [flagData, selectedFeature]);

  const selectedFeatureEnvironmentSetting = useMemo(() => {
    if (!selectedFeatureDefinition) {
      return "Default";
    }

    const environmentOverride = selectedFeatureDefinition.environmentOverrides.find(
      (item) => item.environment === environment,
    )?.value;

    if (environmentOverride === true) {
      return "Enabled";
    }

    if (environmentOverride === false) {
      return "Disabled";
    }

    return "Default";
  }, [environment, selectedFeatureDefinition]);

  const featureColumns: Column<ClientFeatureFlagDefinition>[] = useMemo(
    () => [
      { header: "Feature", accessor: (definition) => getFeatureDisplayName(definition), bold: true, wrap: true },
      {
        header: "Default Value",
        accessor: ({ defaultValue }) => settingToDisplayValue(defaultValue),
        wrap: true,
      },
      {
        header: "Status",
        accessor: ({ stage }) => stage,
        cell: ({ row }) => {
          const mapping = mapFeatureStatusToDisplayTextAndColor(row.original.stage);
          return <Capsule label={mapping.text} size="small" backgroundColor={mapping.color} color="white" />;
        },
        wrap: true,
      },
    ],
    [],
  );

  const statusFilterOptions: OptionProps<string>[] = [
    { label: "All statuses", value: "all" },
    { label: "Experimental", value: "experimental" },
    { label: "Alpha", value: "alpha" },
    { label: "Closed Beta", value: "closed_beta" },
    { label: "Open Beta", value: "open_beta" },
    { label: "GA", value: "general_availability" },
    { label: "Deprecated", value: "deprecated" },
  ];

  const environmentFilterOptions: OptionProps<string>[] = [
    { label: "All env settings", value: "all" },
    { label: "Enabled", value: "enabled" },
    { label: "Disabled", value: "disabled" },
    { label: "Default", value: "default" },
  ];

  const stalenessFilterOptions: OptionProps<string>[] = [
    { label: "All staleness", value: "all" },
    { label: "Fresh", value: "fresh" },
    { label: "Aging", value: "aging" },
    { label: "Stale", value: "stale" },
  ];

  const lifecycleFilterOptions: OptionProps<string>[] = [
    { label: "Active flags", value: "active" },
    { label: "Retired flags", value: "retired" },
  ];

  const filteredFeatures = useMemo(() => {
    const definitions = featureDefinitions ?? [];

    return filterList(
      definitions.filter((definition) => {
        const envSetting = definition.environmentOverrides.find((item) => item.environment === environment);
        let currentEnvironmentSetting = "default";
        if (envSetting?.value === true) {
          currentEnvironmentSetting = "enabled";
        }
        if (envSetting?.value === false) {
          currentEnvironmentSetting = "disabled";
        }

        if (statusFilter !== "all" && definition.stage !== statusFilter) {
          return false;
        }

        if (lifecycleFilter === "active" && definition.retiredAt) {
          return false;
        }

        if (lifecycleFilter === "retired" && !definition.retiredAt) {
          return false;
        }

        if (environmentFilter !== "all" && currentEnvironmentSetting !== environmentFilter) {
          return false;
        }

        const featureStaleness = flagData?.featureFlags?.[definition.key]?.stalenessLabel;
        if (stalenessFilter !== "all" && featureStaleness !== stalenessFilter) {
          return false;
        }

        if (definition.hideWhenDisabled && envSetting?.value === false) {
          return false;
        }

        return true;
      }),
      search,
    );
  }, [
    environment,
    environmentFilter,
    featureDefinitions,
    flagData,
    lifecycleFilter,
    search,
    stalenessFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (featureDefinitionsLoading) {
      return;
    }

    if (!featureDefinitions || featureDefinitions.length === 0) {
      if (selectedFeature !== null) {
        setSelectedFeature(null);
      }
      return;
    }

    const selectedFromUrl = featureDefinitions.find((item) => item.key === feature);
    const nextFeature = selectedFromUrl?.key ?? featureDefinitions[0].key;
    if (selectedFeature !== nextFeature) {
      setSelectedFeature(nextFeature);
    }
  }, [feature, featureDefinitions, featureDefinitionsLoading, selectedFeature, setSelectedFeature]);

  const refreshFeatureDefinitions = async (): Promise<void> => {
    await mutateFeatureDefinitions();
  };

  const handleDeleteRetiredFlag = async (): Promise<void> => {
    if (!selectedFeatureDefinition) {
      return;
    }

    setIsDeletingRetired(true);
    const [, message] = await deleteFeatureFlag(selectedFeatureDefinition.key);
    setIsDeletingRetired(false);

    if (message) {
      return;
    }

    setShowDeleteRetiredModal(false);
    await refreshFeatureDefinitions();
    void navigate({ to: location.pathname });
  };

  let leftContent: ReactElement;
  if (leftPaneLoading) {
    leftContent = <Loading />;
  } else if (filteredFeatures.length > 0) {
    leftContent = (
      <ConfigTable
        columns={featureColumns}
        data={filteredFeatures}
        onRowClick={(row) => void navigate({ to: `${location.pathname}?feature=${row.original.key}` })}
        isRowSelected={(row) => selectedFeature === row.original.key}
      />
    );
  } else {
    leftContent = (
      <BuilderPortalEmptyCTA>
        <div className="cta-container">
          <div className="cta-title">No features found.</div>
        </div>
      </BuilderPortalEmptyCTA>
    );
  }

  assertIsNotNullOrUndefined(user);

  return (
    <AdminPage currentPage="features" pageTitle="Admin - Feature Flags">
      <SplitView
        leftWidthPercent={35}
        left={
          <div className={featureFlagListStyles.wrapper}>
            <AdminTableHeader
              title="Feature Flags"
              search={
                <SearchInput
                  type="text"
                  inputSize="medium"
                  placeholder="Search"
                  clearSearch={() => setSearch("")}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  value={search}
                  isInHeader
                />
              }
            />
            <p className={styles.description}>
              Feature flag defaults are managed in the feature flag service, and can be overridden at the environment,
              builder, or user level.{" "}
              <Link target="_blank" rel="noreferrer" className={styles.descriptionLink} href={DOC_URLS.FEATURE_FLAGS}>
                Read the documentation for more information
              </Link>
              .
            </p>
            <div className={styles.filtersRow}>
              <Select
                options={lifecycleFilterOptions}
                value={lifecycleFilter}
                onChange={(selection) => {
                  if (typeof selection === "string") {
                    setLifecycleFilter(selection);
                  }
                }}
                selectSize="medium"
              />
              <Select
                options={statusFilterOptions}
                value={statusFilter}
                onChange={(selection) => {
                  if (typeof selection === "string") {
                    setStatusFilter(selection);
                  }
                }}
                selectSize="medium"
              />
            </div>
            <div className={styles.filtersRow}>
              <Select
                options={environmentFilterOptions}
                value={environmentFilter}
                onChange={(selection) => {
                  if (typeof selection === "string") {
                    setEnvironmentFilter(selection);
                  }
                }}
                selectSize="medium"
              />
              <Select
                options={stalenessFilterOptions}
                value={stalenessFilter}
                onChange={(selection) => {
                  if (typeof selection === "string") {
                    setStalenessFilter(selection);
                  }
                }}
                selectSize="medium"
              />
            </div>
            {leftContent}
          </div>
        }
        right={
          selectedFeature ? (
            <div className={featureFlagOverviewStyles.wrapper}>
              <div className={styles.header}>
                <h1 className={styles.title}>
                  {selectedFeatureDefinition
                    ? getFeatureDisplayName(selectedFeatureDefinition)
                    : camelCaseToTitleCase(selectedFeature)}
                </h1>
                <Capsule
                  color="white"
                  backgroundColor={mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition?.stage ?? "").color}
                  label={mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition?.stage ?? "").text}
                  size="small"
                />
              </div>
              <div className={styles.meta}>
                {selectedFeatureDefinition?.retiredAt ? (
                  <Button
                    disabled={!userIsSystemAdmin(user)}
                    title="Only System Admins can delete retired feature flags."
                    btnStyle="remove"
                    btnSize="small"
                    onClick={() => setShowDeleteRetiredModal(true)}
                  >
                    Delete retired flag
                  </Button>
                ) : null}
                <span className={styles.metaOwnerText}>{selectedFeatureDefinition?.ownerName ?? FALLBACK_VALUE}</span>
                <span className={styles.divider} />
                <span className={styles.metaText}>{selectedFeatureDefinition?.description ?? FALLBACK_VALUE}</span>
                {selectedFeatureDefinition?.documentationUrl ? (
                  <>
                    <span className={styles.divider} />
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      className={styles.metaDocumentationLink}
                      href={selectedFeatureDefinition.documentationUrl}
                    >
                      View Documentation
                    </Link>
                  </>
                ) : null}
              </div>
              <div className={styles.tabRow}>
                <TabbedMenu menuItems={menuItems} />
              </div>
              <HorizontalRule />
              {rightPaneLoading || (!selectedFeatureDefinition && !featureDefinitionsLoading) ? (
                <Loading />
              ) : (
                <>
                  {tab === "settings" && selectedFeatureDefinition && (
                    <div className={styles.description}>
                      {selectedFeatureDefinition.canEdit ? (
                        <FeatureFlagDefinitionEditor
                          selectedFeatureDefinition={selectedFeatureDefinition}
                          onDefinitionUpdated={refreshFeatureDefinitions}
                        />
                      ) : (
                        <div className={styles.readOnlySettings}>
                          <p className={styles.readOnlyMessage}>
                            You no longer have edit access for this feature flag. Settings remain visible in read-only
                            mode.
                          </p>
                          <div className={styles.readOnlyGrid}>
                            <div>
                              <span className={styles.readOnlyLabel}>Owner</span>
                              <div>{selectedFeatureDefinition.ownerName ?? FALLBACK_VALUE}</div>
                              <div className={styles.readOnlySubtext}>
                                {selectedFeatureDefinition.ownerEmail ?? FALLBACK_VALUE}
                              </div>
                            </div>
                            <div>
                              <span className={styles.readOnlyLabel}>Display name</span>
                              <div>{selectedFeatureDefinition.displayName ?? FALLBACK_VALUE}</div>
                            </div>
                            <div>
                              <span className={styles.readOnlyLabel}>Status</span>
                              <div>{mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition.stage).text}</div>
                            </div>
                            <div>
                              <span className={styles.readOnlyLabel}>Environment setting</span>
                              <div>{selectedFeatureEnvironmentSetting}</div>
                            </div>
                            <div>
                              <span className={styles.readOnlyLabel}>Default setting</span>
                              <div>{settingToDisplayValue(selectedFeatureDefinition.defaultValue)}</div>
                            </div>
                            <div>
                              <span className={styles.readOnlyLabel}>Allow per-user overrides</span>
                              <div>{settingToDisplayValue(selectedFeatureDefinition.allowPerUserEnablement)}</div>
                            </div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Description</span>
                            <div>{selectedFeatureDefinition.description ?? FALLBACK_VALUE}</div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Managers allowlist</span>
                            {selectedFeatureDefinition.editorAllowlistEmails.length > 0 ? (
                              <ul className={styles.readOnlyList}>
                                {selectedFeatureDefinition.editorAllowlistEmails.map((email) => {
                                  return <li key={email}>{email}</li>;
                                })}
                              </ul>
                            ) : (
                              <div>{FALLBACK_VALUE}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {tab === "builders" && (
                    <BuilderFeatureFlagsList
                      builders={ensureDefined(builders)}
                      selectedFeatureDefinition={selectedFeatureDefinition}
                      onUpdate={mutateBuilders}
                    />
                  )}
                  {tab === "users" && (
                    <UserFeatureFlagsList
                      users={ensureDefined(users)}
                      selectedFeatureDefinition={selectedFeatureDefinition}
                      onUpdate={mutateUsers}
                      setUserSearch={setUserSearch}
                      pagination={pagination}
                      setPagination={setPagination}
                      orderBy={orderBy}
                      setOrderBy={setOrderBy}
                    />
                  )}
                  {tab === "metrics" && (
                    <>
                      {selectedFeatureDefinition && selectedFeatureMetrics ? (
                        <>
                          <div className={styles.metricsGrid}>
                            {METRIC_CARD_CONFIG.map((card) => {
                              return (
                                <div key={card.key} className={styles.metricCard}>
                                  <div className={styles.metricLabel}>
                                    <TextWithTooltipIcon text={card.label} tooltip={card.tooltip} />
                                  </div>
                                  <div className={styles.metricValue}>{selectedFeatureMetrics[card.key]}</div>
                                </div>
                              );
                            })}
                          </div>
                          <div className={styles.stalenessCard}>
                            <TextWithTooltipIcon
                              text="Staleness score"
                              tooltip="Score is calculated server-side from days since the last stage change, normalized by stage freshness windows: Experimental 14d, Alpha 30d, Closed Beta 45d, Open Beta 60d, General Availability 120d, Deprecated 30d."
                            />
                            <div className={styles.metricValue}>{selectedFeatureMetrics.stalenessScore}</div>
                            <div className={styles.metricSubtext}>
                              {camelCaseToTitleCase(selectedFeatureMetrics.stalenessLabel)} -{" "}
                              {selectedFeatureMetrics.stalenessDaysSinceStageUpdated} days since stage update
                            </div>
                          </div>
                        </>
                      ) : (
                        <BuilderPortalEmptyCTA>
                          <div className="cta-container">
                            <div className="cta-title">No metrics available for this feature yet.</div>
                          </div>
                        </BuilderPortalEmptyCTA>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className={styles.emptyBox} />
          )
        }
      />
      {showDeleteRetiredModal && selectedFeatureDefinition ? (
        <ConfirmDeleteModal
          onClose={() => setShowDeleteRetiredModal(false)}
          onConfirm={async (e) => {
            e.preventDefault();
            await handleDeleteRetiredFlag();
          }}
          isDeleting={isDeletingRetired}
          entityType="retired feature flag"
          entityName={getFeatureDisplayName(selectedFeatureDefinition)}
          message={
            <div>
              Are you sure you want to permanently delete this retired flag from the database? This cannot be undone.
            </div>
          }
        />
      ) : null}
    </AdminPage>
  );
}

export const Route = createFileRoute("/admin/features")({
  component: withAuthentication(AdminFeatureFlagsPage, { superAdmin: true }),
});
